import { LineStyle, PolylineProps } from '@editor/interfaces/canvas'
import { LINE_WIDTH_TO_GAP_RATIO } from '@editor/pages/projects/blueprint-editor/constants'

import CircleAnchor from '../EditorCircleAnchor'
import Label from '../Label'
import Anchor from './components/Anchor'
import { DEFAULT_LINE_MATERIAL } from './constants'
import useConfigs from './hooks/useConfigs'
import useEventHandlers from './hooks/useEventHandlers'
import useObjects from './hooks/useObjects'

/**
 * Polyline object with ability to add labels and anchors.
 * Labels will be displayed at the midpoint of the polyline.
 * Anchor will be displayed at the start and end of the polyline.
 */
const Polyline = (props: PolylineProps) => {
  const {
    id,

    vertexAnchors,
    anchorProps,
    topAnchorProps,
    bottomAnchorProps,
    hideBottomAnchor,
  } = props
  const configs = useConfigs(props)
  const {
    labelConfig,
    segmentLabelsConfig,
    lineConfig,
    lineOutlineConfig,

    isRemovingVertex,
    isDraggable,
    isHoverSelected,
    isAddingVertex,
    isVertexRemovable,
    isMergingPolyline,
    isNoVertexActions,
    isDraggingVertexRef,
    isHoveringAnchorRef,
    currentPositionsRef,

    pointOnLine,

    pointIndexForMerge,
    setPointIndexForMerge,

    canBeSeen,
    canBeInteracted,
    canLineBeInteracted,
  } = configs
  const { handleMouseEnter, handleMouseLeave, handleMouseMove, handleMouseDown, handleClick } = useEventHandlers(
    props,
    configs,
  )
  const {
    groupRef,
    hitboxLineRef,
    lineRef,
    outlineRef,
    editingAnchorRef,

    edgeHighlightRef,
    edgeGeometry,
    isEdgeHighlightVisible,

    polylinePoints,
    isVertexVisible,

    labelData,
    labelMessage,
    segmentLabelData,
    labelAdjustmentAngle,
  } = useObjects(props, configs)

  return (
    <group ref={groupRef} name={`distance-label-${id}-group`} visible={canBeSeen}>
      <line2
        ref={hitboxLineRef}
        renderOrder={10}
        name={`distance-label-${id}-hitbox`}
        onPointerEnter={canLineBeInteracted ? handleMouseEnter : undefined}
        onPointerLeave={handleMouseLeave} // always allow mouse leave to reset state
        onPointerDown={canLineBeInteracted ? handleMouseDown : undefined}
        onPointerMove={canLineBeInteracted ? handleMouseMove : undefined}
        onClick={canLineBeInteracted ? handleClick : undefined}
      >
        <lineMaterial
          transparent
          alphaToCoverage
          worldUnits
          color="pink"
          linewidth={(lineOutlineConfig?.thickness || lineConfig?.thickness || 0.002) * 3}
          // dashed={LineStyle.Solid} // hitbox is always solid
          depthTest={false}
          depthWrite={false}
          visible={false}
        />
      </line2>

      <line2
        ref={edgeHighlightRef}
        renderOrder={20}
        name={`distance-label-${id}-edge-highlight`}
        geometry={edgeGeometry}
        raycast={() => null}
      >
        <lineMaterial
          transparent
          alphaToCoverage
          worldUnits
          color="yellow"
          linewidth={lineOutlineConfig?.thickness || 0.002}
          dashed={false}
          depthTest={false}
          depthWrite={false}
          visible={isRemovingVertex && isEdgeHighlightVisible}
        />
      </line2>

      <line2 ref={outlineRef} renderOrder={10} name={`distance-label-${id}-outline`} raycast={() => null}>
        <lineMaterial
          transparent
          alphaToCoverage
          worldUnits
          color={lineOutlineConfig?.color === 'transparent' ? 'white' : lineOutlineConfig?.color || 'white'}
          linewidth={lineOutlineConfig?.thickness || 0.002}
          dashed={lineOutlineConfig?.style === LineStyle.Dashed}
          dashScale={2}
          dashSize={2}
          gapSize={1.5}
          depthTest={false}
          depthWrite={false}
          visible={lineOutlineConfig?.visible ?? lineOutlineConfig?.color !== 'transparent'}
        />
      </line2>

      <line2 ref={lineRef} name={`distance-label-${id}-line`} renderOrder={15} raycast={() => null}>
        <lineMaterial
          transparent
          alphaToCoverage
          worldUnits
          color={lineConfig?.color === 'transparent' ? 'white' : lineConfig?.color || 'white'}
          dashed={lineConfig?.style === LineStyle.Dashed}
          linewidth={lineConfig?.thickness || 0.001}
          dashScale={DEFAULT_LINE_MATERIAL.dashScale}
          dashSize={(lineConfig?.thickness || 0.001) * LINE_WIDTH_TO_GAP_RATIO}
          gapSize={(lineConfig?.thickness || 0.001) * LINE_WIDTH_TO_GAP_RATIO}
          depthTest={false}
          depthWrite={false}
          visible={lineConfig?.visible ?? true}
        />
      </line2>

      {isAddingVertex && pointOnLine ? (
        <CircleAnchor
          id={`${id}-current-anchor`}
          point={pointOnLine}
          tooltipText="頂点を追加"
          showTooltip="always"
          isInteractable={false}
          renderOrder={20}
        />
      ) : null}

      {vertexAnchors &&
        polylinePoints.slice(0, hideBottomAnchor ? -1 : undefined).map(({ id: anchorId, point }, anchorIndex) => (
          <Anchor
            {...vertexAnchors}
            {...anchorProps}
            {...(anchorIndex === 0 ? topAnchorProps : undefined)}
            {...(anchorIndex === polylinePoints.length - 1 ? bottomAnchorProps : undefined)}
            key={anchorId}
            id={anchorId}
            visible={isVertexVisible(anchorIndex)}
            parentId={id}
            anchorIndex={anchorIndex}
            point={point.toArray()}
            isNoAction={isNoVertexActions}
            isDraggable={isDraggable}
            isParentHoverSelected={isHoverSelected}
            isHighlighted={pointIndexForMerge === anchorIndex}
            onEnter={(idx, ev) => {
              isHoveringAnchorRef.current = true
              vertexAnchors.onEnter?.(idx, ev)
            }}
            onLeave={(idx, ev) => {
              isHoveringAnchorRef.current = false
              vertexAnchors.onLeave?.(idx, ev)
            }}
            onUpdate={
              canBeInteracted || vertexAnchors.isInteractable || isDraggable
                ? (idx, pt) => {
                    const pts =
                      currentPositionsRef.current?.slice() || polylinePoints.map(({ point: p }) => p.toArray())
                    pts[idx] = pt
                    vertexAnchors.onUpdate?.(pts)
                    isDraggingVertexRef.current = true
                  }
                : undefined
            }
            onUp={(aidx, ev) => {
              if (isVertexRemovable) {
                vertexAnchors.onUpdated?.(
                  polylinePoints.filter((_, i) => i !== aidx).map(({ point: p }) => p.toArray()),
                )
              } else if (isMergingPolyline) {
                setPointIndexForMerge((prev) => (prev === aidx ? null : aidx))
              }
              vertexAnchors.onUp?.(aidx, ev)
            }}
            onDown={(aidx, ev) => {
              if (isDraggable) editingAnchorRef.current = aidx
              vertexAnchors.onDown?.(aidx, ev)
            }}
            onComplete={() => {
              if (currentPositionsRef.current) {
                vertexAnchors.onUpdated?.(currentPositionsRef.current)
              }

              editingAnchorRef.current = null
              currentPositionsRef.current = null
              labelMessage.current = null
              isHoveringAnchorRef.current = false
              isDraggingVertexRef.current = false
            }}
          />
        ))}

      {labelData && labelConfig ? (
        <Label
          id={`${id}-distance-label`}
          point={labelData.point}
          label={labelMessage.current || labelConfig.label}
          labelPrefix=""
          visible={labelConfig.visible && canBeSeen}
          styles={labelConfig.styles}
          isAdjustingColors={labelConfig.isAdjustingColors}
          autoAdjustAwayFromAngle={labelAdjustmentAngle}
        />
      ) : null}

      {segmentLabelsConfig && segmentLabelData.length > 0
        ? segmentLabelData.map(({ id: segmentId, point, label }) => (
            <Label
              key={`${id}-segment-distance-label-${segmentId}`}
              id={`${id}-segment-distance-label-${segmentId}`}
              point={point}
              label={label}
              labelPrefix=""
              visible={segmentLabelsConfig.visible && canBeSeen}
              styles={segmentLabelsConfig.styles}
              isAdjustingColors={segmentLabelsConfig.isAdjustingColors}
            />
          ))
        : null}
    </group>
  )
}

export default Polyline
